import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import CommonTables from "../../Commoncomponents/CommonTables";
import PageWrapper from "../../Components/PageWrapper";
import Layout from "../../Layout/Layout";
import { formatDate } from "../../Commoncomponents/CommonComponents";
import { fetchData, fetchAllData } from "../../helpers/externapi";

const shimmerStyle = `
  @keyframes shimmer {
    0% { background-position: -468px 0; }
    100% { background-position: 468px 0; }
  }
  .shimmer {
    animation-duration: 1.0s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: shimmer;
    animation-timing-function: linear;
    background: #F7F7F7;
    background: linear-gradient(to right, #f0f0f0 8%, #fafafa 18%, #f0f0f0 33%);
    background-size: 1000px 104px;
    position: relative;
    overflow: hidden;
  }
  .shimmer-container {
    background-color: #F7F7F7;
    border-radius: 4px;
    height: 50px;
    width: 100%;
    margin: 15px;
  }
  .shimmer-text {
    background-color: #C8C8C8;
    border-radius: 4px;
    height: 15px;
    width: 15%;
    margin: 15px 0 0 15px;
  }
  .shimmer-text2 {
    background-color: #C8C8C8;
    border-radius: 4px;
    height: 15px;
    width: 55%;
    margin: 15px 0 0 15px;
    position: relative;
    left: 10%;
    bottom: 10%;
  }
  .shimmer-row { display: flex; }
  .shimmer-cell {
    flex: 1;
    padding: 10px;
    height: 50px;
    background-color: #F7F7F7;
    border-radius: 4px;
    margin: 5px;
  }
`;

export default function OHOCardAssignmentHistoryList() {
    const [loading, setLoading] = useState(false);
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [input, setInput] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState("");

    const tableHeads = [
        "OHO CARD NUMBER",
        "CUSTOMER NAME",
        "ASSIGNED BY",
        "ASSIGNED TO",
        "ASSIGNED DATE",
        "CREATED DATE",
    ];

    const fetchAllHistory = async () => {
        setLoading(true);
        try {
            const data = await fetchAllData(
                "lambdaAPI/OHOCardAssignmentHistory/GetAllOHOCardAssignmentHistory"
            );

            setAllData(data || []);
            setFilteredData(data || []);
            setTotalCount((data || []).length);
        } catch (err) {
            console.error("Error fetching assignment history:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllHistory();
    }, []);

    const paginatedData = filteredData.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage
    );

    const tableElements =
        paginatedData.length > 0
            ? paginatedData.map((row) => [
                row.OHOCardNumber ?? "—",
                row.Name ?? "—",
                row.AssignedBy ?? "—",
                row.AssignedTo ?? "—",
                row.AssignedDate
                    ? formatDate(row.AssignedDate).slice(0, 11)
                    : "—",
                row.CreatedDate
                    ? formatDate(row.CreatedDate).slice(0, 11)
                    : "—",
            ])
            : [];

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInput(value);
        setSearchError("");

        if (value.length >= 2) {
            setSearchLoading(true);
            const matched = allData.filter((row) =>
                (row.OHOCardNumber ?? "")
                    .replace(/\s/g, "")
                    .includes(value.replace(/\s/g, ""))
            );
            setSuggestions(matched.slice(0, 8));
            if (matched.length === 0) setSearchError("No suggestions found.");
            setSearchLoading(false);
        } else {
            setSuggestions([]);
            if (value.length === 0) {
                setFilteredData(allData);
                setTotalCount(allData.length);
                setCurrentPage(1);
            }
        }
    };

    const handleSuggestionClick = (row) => {
        const matched = allData.filter(
            (r) => r.OHOCardNumber === row.OHOCardNumber
        );
        setFilteredData(matched);
        setTotalCount(matched.length);
        setCurrentPage(1);
        setInput(row.OHOCardNumber ?? "");
        setSuggestions([]);
    };

    const clearSearch = () => {
        setInput("");
        setSuggestions([]);
        setSearchError("");
        setFilteredData(allData);
        setTotalCount(allData.length);
        setCurrentPage(1);
    };

    const handlePageChange = (_e, page) => setCurrentPage(page);
    const handlePerPageChange = (e) => {
        setPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const skeletonLoading = () => (
        <>
            <style>{shimmerStyle}</style>
            <div className="shimmer-container shimmer">
                <h6 className="shimmer-text" />
            </div>
            <table className="shimmer-container shimmer">
                <tbody>
                    {Array.from({ length: 6 }).map((_, ri) => (
                        <tr key={ri} className="shimmer-row">
                            {Array.from({ length: 6 }).map((_, ci) => (
                                <td key={ci} className="shimmer-cell">
                                    <h6 className="shimmer-text2" />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );

    return (
        <Layout>
            <PageWrapper>
                {loading && skeletonLoading()}

                {!loading && (
                    <>

                        <div className="card mb-2 mt-2">
                            <div className="card-body d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 style={{ margin: 0, fontWeight: "800", color: "#00796b" }}>
                                        {/*<i className="fas fa-history" style={{ marginRight: "10px" }}></i>*/}
                                        OHOCards History
                                    </h4>
                                    <small style={{ color: "#6c757d" }}>
                                        View and search all assigned OHOCard records
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* ── Search bar ── */}
                        <div className="card mt-2 mb-2">
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-12 mb-2">
                                        <label htmlFor="search-input" className="form-label">
                                            Search by OHO Card Number
                                        </label>
                                        <div style={{ position: "relative", maxWidth: "420px" }}>
                                            <input
                                                type="text"
                                                id="search-input"
                                                className="form-control"
                                                style={{ paddingLeft: "30px" }}
                                                placeholder="e.g. 2804 0000 0000"
                                                value={input}
                                                onChange={handleInputChange}
                                            />

                                            {/* Clear icon */}
                                            {input && (
                                                <i
                                                    className="fas fa-times-circle"
                                                    style={{
                                                        position: "absolute",
                                                        right: "10px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        fontSize: "16px",
                                                        color: "red",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={clearSearch}
                                                />
                                            )}

                                            {/* Search spinner */}
                                            {searchLoading && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        right: "40px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                    }}
                                                >
                                                    <div
                                                        className="spinner-border text-primary"
                                                        role="status"
                                                        style={{ width: "1.5rem", height: "1.5rem" }}
                                                    >
                                                        <span className="visually-hidden">Loading…</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Suggestion dropdown */}
                                            {input && !searchLoading && suggestions.length > 0 && (
                                                <ul
                                                    style={{
                                                        listStyleType: "none",
                                                        padding: 0,
                                                        margin: 0,
                                                        border: "1px solid #00796b",
                                                        borderRadius: "4px",
                                                        maxHeight: "220px",
                                                        overflowY: "auto",
                                                        position: "absolute",
                                                        width: "100%",
                                                        backgroundColor: "#fff",
                                                        zIndex: 10,
                                                        top: "100%",
                                                        left: 0,
                                                    }}
                                                >
                                                    {suggestions.map((row, i) => (
                                                        <li
                                                            key={i}
                                                            onClick={() => handleSuggestionClick(row)}
                                                            style={{
                                                                padding: "8px",
                                                                cursor: "pointer",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                borderBottom: "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <i
                                                                className="fas fa-credit-card"
                                                                style={{ marginRight: "8px", color: "#00796b" }}
                                                            />
                                                            <span style={{ flex: 1 }}>
                                                                <strong>{row.OHOCardNumber}</strong>
                                                                {row.Name ? ` — ${row.Name}` : ""}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}

                                            {/* No results */}
                                            {input && !searchLoading && searchError && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "100%",
                                                        left: 0,
                                                        color: "red",
                                                        fontSize: "0.9rem",
                                                        marginTop: "4px",
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #00796b",
                                                        borderRadius: "4px",
                                                        padding: "8px",
                                                        zIndex: 10,
                                                        width: "100%",
                                                    }}
                                                >
                                                    {searchError}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Table ── */}
                        <div className="row" style={{ margin: 0, padding: 0 }}>
                            <div
                                className="col-xl-12 col-lg-12 col-md-12 order-0 order-md-1"
                                style={{ margin: 0, padding: 0 }}
                            >
                                <div className="card mb-4" style={{ margin: 0, padding: 0 }}>
                                    <div
                                        className="card-body"
                                        style={{ margin: 0, padding: 0, position: "relative" }}
                                    >
                                        {filteredData.length === 0 && (
                                            <div style={{ textAlign: "center", padding: "20px" }}>
                                                There are no records to display.
                                            </div>
                                        )}

                                        {filteredData.length > 0 && (
                                            <CommonTables
                                                tableHeads={tableHeads}
                                                tableData={tableElements}
                                                perPage={perPage}
                                                currentPage={currentPage}
                                                perPageChange={handlePerPageChange}
                                                pageChange={handlePageChange}
                                                totalCount={totalCount}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </PageWrapper>
        </Layout>
    );
}